"use client";

import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import { debounce, isArray } from "lodash";
import { api } from "../../api/api";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import type { IOpportunities } from "../../redux/opportunites/opportunites.types";
import { defaultOpportunity } from "../../redux/opportunites/deafaultState";
import type { IVenueAutoSearch } from "./AutoCompleteSearches.types";
import { useBillingCompanyContext } from "../../providers";

export const VenueAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  IVenueAutoSearch
>((props, ref) => {
  const {
    value,
    disabled,
    label,
    venue_name,
    onSelect,
    error,
    isHighlighted = false,
    sx,
    InputLabelProps,
    multiple = false,
  } = props;
  const [options, setOptions] = useState<readonly IOpportunities[]>([]);
  const [loading, setLoading] = useState(false);
  const companyOutlet = useBillingCompanyContext();
  const [search, setSearchText] = useState<string>("");
  const initialLoad = useRef(false);
  const [key, setKey] = React.useState(0);

  const fetchSuggestion = async (enteredValue: string) => {
    setLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `column=venue_name&value=${enteredValue}&`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/venue/get-venue?${searchQuery}pageNo=1&itemPerPage=10&company_outlet_uuid=${company_outlet_uuid}&status=ACTIVE`;

      const res = await api.get(url);
      const data: IOpportunities[] = res.data.data;
      if (isArray(data)) {
        setOptions(data);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = React.useMemo(
    () => debounce(fetchSuggestion, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );
  useEffect(() => () => debounceFn.cancel(), [debounceFn]);

  const getOptionLabel = (option: string | IOpportunities) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.venue_name) {
      return `${option.venue_name}`;
    }
    return `${option.venue_uuid || ""}`;
  };

  useEffect(() => {
    if (search && search !== value) {
      debounceFn(search);
    }
  }, [search, value, debounceFn]);

  const handleFocus = () => {
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setKey((prev) => prev + 1);
      onSelect?.(defaultOpportunity);
    },
  }));

  React.useEffect(() => {
    if (value === "") {
      setSearchText("");
      setKey((prev) => prev + 1);
    }
  }, [value]);

  return (
    <>
      {/* {label && <CustomFormLabel>{label}</CustomFormLabel>} */}
      <EnrAutoComplete<IOpportunities, false, false, true>
        id="venue-auto-search"
        fullWidth
        key={key}
        freeSolo
        sx={sx}
        isHighlighted={isHighlighted}
        label={label}
        placeholder="Search by Venue"
        error={typeof error === "string" ? error : undefined}
        loading={loading}
        onFocus={handleFocus}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          typeof option === "string"
            ? option === value
            : typeof option !== "string" &&
              typeof value !== "string" &&
              option.venue_uuid === value.venue_uuid
        }
        filterOptions={(x) => x}
        options={options as IOpportunities[]}
        autoComplete
        value={
          (options.find((option) => option.venue_uuid === value) ||
            venue_name ||
            "") as any
        }
        disabled={disabled}
        noOptionsText="Type to search"
        onChange={(event, value: string | IOpportunities | null) => {
          if (typeof value === "object" && value !== null) {
            onSelect?.(value);
          } else {
            onSelect?.(defaultOpportunity);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if ((event && event.type === "change") || !newInputValue) {
            setSearchText(newInputValue);
          }
        }}
      />
    </>
  );
});
